/**
 * The places FTUX finds on the studio's pages: real elements carry `data-ftux="<id>"`, and Lumio's card anchors to
 * them, their light comes on, and the zones that take the owner's clicks let them through while the rest of the page
 * holds. Pages take ids from FTUX_TARGETS only. Shared Button and Toggle forward no data-* attribute: wrap them in
 * FtuxTarget. Outside an FTUX step the attributes are inert. The menu's ids follow the studio's menu (studioMenu.ts):
 * a door added there has its targets here with no FTUX edit.
 */
import {
  PHONE_BAR_DOORS,
  PHONE_DRAWER_DOORS,
  type PhoneBarDoorKey,
  type PhoneDrawerDoorKey,
} from '../../layouts/MainLayout/studioMenu';
import type { HubArea } from '../../theme/tokens';
import { FTUX_PAGE_CONTROLS, isNavId, NAV_IDS, type AdArea, type NavId } from './catalog';

/** The attribute FTUX looks its targets up by. */
export const FTUX_ATTR = 'data-ftux';

/** The rows of the phone's More drawer, by their door's key (the studio's menu). */
export const MNAV_MORE_KEYS: readonly PhoneDrawerDoorKey[] = PHONE_DRAWER_DOORS;
export type MnavMoreKey = PhoneDrawerDoorKey;

const isMnavMoreKey = (value: string): value is MnavMoreKey => (MNAV_MORE_KEYS as readonly string[]).includes(value);

/** The side rail's Items (from 900 px), one per menu entry (SideMenu renderItem). */
const RAIL_ITEMS = NAV_IDS.map((key): `rail-${NavId}` => `rail-${key}`);
/** The phone's bottom bar doors (MobileNav). */
const BAR_DOORS = PHONE_BAR_DOORS.map((key): `mnav-${PhoneBarDoorKey}` => `mnav-${key}`);
/** The phone's More drawer rows (MobileNav). */
const DRAWER_ROWS = PHONE_DRAWER_DOORS.map((key): `mnav-more-${PhoneDrawerDoorKey}` => `mnav-more-${key}`);

/** Every FTUX target id. */
export const FTUX_TARGETS = [
  // The side rail: its Items, then its More trigger.
  ...RAIL_ITEMS,
  'rail-more',
  // Where Lumio lives: the top bar's Lumio trigger (tablet, phone, collapsed dock) and the docked chat's mark.
  'lumio-place',
  // The phone: the bottom bar, its doors and its More, then the More drawer's rows.
  'mnav-bar',
  ...BAR_DOORS,
  'mnav-more',
  ...DRAWER_ROWS,
  // Home's Spotlight: the waiting piece's pick card.
  'spotlight-pick',
  // The hub cards no piece opens (every WebsiteHubCard carries its hub target).
  'hub-games',
  'hub-landing-pages',
  'hub-seo',
  // The page parts a piece lights without taking a click there: the Art Style's ingredients (a dialog target, see
  // targetZone) and the Mailbox section's grid.
  'style-ingredients',
  'mail-section',
  // The page controls a piece lights, described in catalog.ts (the hub cards it opens among them).
  ...FTUX_PAGE_CONTROLS,
] as const;
export type FtuxTargetId = (typeof FTUX_TARGETS)[number];

/** The CSS selector of a target's markers. */
export const ftuxSelector = (id: FtuxTargetId): string => `[${FTUX_ATTR}='${id}']`;

/** The rail Item of a menu entry. A SideMenu key that is no menu entry of the FTUX gives undefined (no marker). */
export function railTarget(nav: NavId): FtuxTargetId;
export function railTarget(key: string): FtuxTargetId | undefined;
export function railTarget(key: string): FtuxTargetId | undefined {
  return isNavId(key) ? `rail-${key}` : undefined;
}

/** A door of the phone's bottom bar, by its key. */
export const mnavTarget = (key: PhoneBarDoorKey): FtuxTargetId => `mnav-${key}`;

/** A phone More drawer row, by its key. A drawer label the FTUX never lights gives undefined (no marker). */
export function mnavMoreTarget(key: MnavMoreKey): FtuxTargetId;
export function mnavMoreTarget(key: string): FtuxTargetId | undefined;
export function mnavMoreTarget(key: string): FtuxTargetId | undefined {
  return isMnavMoreKey(key) ? `mnav-more-${key}` : undefined;
}

/** A Website or Marketing hub card (WebsiteHubCard's Root, by its area). */
export const hubTarget = (area: HubArea): FtuxTargetId => `hub-${area}`;

/** A Marketing library's Create new tile. */
export const adsNewTarget = (area: AdArea): FtuxTargetId => `ads-new-${area}`;

/** The element a page dialog's surface is (the shared Sheet and Modal). */
const DIALOG_SELECTOR = '[role="dialog"]';

/**
 * Targets whose zone is the page dialog round their marker, not the marker. The shared Sheet forwards no attribute
 * and draws its own heading and footer, so the Art Style's ingredients are marked on IngredientsBody and the zone
 * climbs to the sheet's dialog, its Ingredients heading and its Extract style footer included.
 */
const DIALOG_TARGETS: readonly FtuxTargetId[] = ['style-ingredients'];

/** The target's zone is the dialog round its marker (see targetZone). */
export const isDialogTarget = (id: FtuxTargetId): boolean => DIALOG_TARGETS.includes(id);

/**
 * The element a target's zone covers, given one of its markers: the marker itself, or for a dialog target the
 * closest [role="dialog"] round it (the marker when it sits in none). Lookups measure and light this element.
 */
export const targetZone = (id: FtuxTargetId, marker: Element): Element =>
  (isDialogTarget(id) ? marker.closest(DIALOG_SELECTOR) : null) ?? marker;
